import { getWorldAddress } from "@permutation-city/common";
import { indexerUrl } from "@permutation-city/common";
import {
  type UseQueryOptions,
  type UseQueryResult,
  useQuery,
} from "@tanstack/react-query";
import { type Hex, stringify } from "viem";

type DozerResponse = {
  block_height: number;
  result: Array<Array<Array<string | number | boolean>>>;
};

type DozerQueryParams<TData, TSelectData = TData> = {
  sqlQuery: string | string[];
  queryKey?: string[];
  worldAddress?: Hex;
  refetchInterval?: number;
  select?: (data: TSelectData) => TData;
  fields?: Record<string, string>;
  format?: Record<string, (value: string) => unknown>;
  refetchIntervalInBackground?: boolean;
  enabled?: boolean;
};

export async function fetchDozerQuery(sqlQuery: string | string[]) {
  const worldAddress = getWorldAddress();
  const body = Array.isArray(sqlQuery)
    ? sqlQuery.map((query) => ({
        address: worldAddress,
        query,
      }))
    : [
        {
          address: worldAddress,
          query: sqlQuery,
        },
      ];

  const response = await fetch(indexerUrl(), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: stringify(body),
  });

  return response.json() as Promise<DozerResponse>;
}

export function formatDozerResponse<TSelectData>({
  data,
  fields,
  format,
}: {
  data: DozerResponse;
  fields?: Record<string, string> | undefined;
  format?: Record<string, (value: string) => unknown> | undefined;
}) {
  return data.result.map((resultArray) => {
    const columns = resultArray[0] as string[];
    const rows = resultArray.slice(1) as Array<
      Array<string | number | boolean>
    >;

    return rows.map((row) => {
      const mappedRow: Record<string, unknown> = {};
      columns.forEach((column, index) => {
        const value = row[index];
        const newKey = fields?.[column] ?? column;
        const castFn = format?.[column] || format?.[newKey];
        mappedRow[newKey] = castFn ? castFn(value as string) : value;
      });
      return mappedRow;
    });
  }) as TSelectData;
}

export function useDozerQuery<
  TData = unknown,
  TSelectData = [TData],
  TError = unknown,
>(
  {
    sqlQuery,
    queryKey,
    refetchInterval = 10_000,
    refetchIntervalInBackground = false,
    select,
    fields,
    format,
    enabled = true,
  }: DozerQueryParams<TData, TSelectData>,
  options?: Omit<
    UseQueryOptions<DozerResponse, TError, TData>,
    "queryKey" | "queryFn" | "select"
  >,
): UseQueryResult<TData, TError> {
  return useQuery({
    queryKey: queryKey ?? ["dozerQuery", sqlQuery],
    queryFn: async () => {
      return await fetchDozerQuery(sqlQuery);
    },
    select: (data) => {
      const result = formatDozerResponse<TSelectData>({
        data,
        fields,
        format,
      });

      return select ? select(result) : (result as unknown as TData);
    },
    refetchInterval,
    refetchIntervalInBackground,
    enabled,
    staleTime: 0,
    gcTime: 0,
    ...options,
  });
}
